/**
 * Shared user bubble color definitions and utilities.
 * Used by ThemeDialog and anywhere user avatars are displayed.
 */

export const USER_BUBBLE_COLORS = [
  { name: 'Default', value: 'default', textTone: 'auto' as const },
  // Solid colors
  { name: 'Blue', value: '#3366FF', textTone: 'light' as const },        // Blue 50
  { name: 'Purple', value: '#AA44FF', textTone: 'light' as const },      // Purple 50
  { name: 'Pink', value: '#FF44B8', textTone: 'light' as const },        // Magenta 40
  { name: 'Red', value: '#FF4040', textTone: 'light' as const },         // Red 40
  { name: 'Orange', value: '#FF6600', textTone: 'light' as const },      // Orange 50
  { name: 'Yellow', value: '#FFC000', textTone: 'light' as const },      // Amber 50
  { name: 'Green', value: '#00B347', textTone: 'light' as const },       // Green 50
  { name: 'Emerald', value: '#40C975', textTone: 'light' as const },     // Green 40
  { name: 'Teal', value: '#0099CC', textTone: 'light' as const },        // Aqua 60
  { name: 'Cyan', value: '#00BFFF', textTone: 'light' as const },        // Aqua 50
  { name: 'Indigo', value: '#5555FF', textTone: 'light' as const },      // Indigo 50
  { name: 'Gray', value: '#636363', textTone: 'light' as const },        // Gray 70
  // Gradients
  { name: 'Hazy', value: 'linear-gradient(135deg, #6690FF 0%, #7A1FA3 100%)', textTone: 'light' as const },    // Blue 40 → Purple 70
  { name: 'Sunset', value: 'linear-gradient(135deg, #FF8C40 0%, #FFD9BD 100%)', textTone: 'light' as const },  // Orange 40 → Orange 20
  { name: 'Forest', value: 'linear-gradient(135deg, #005C80 0%, #80DBA3 100%)', textTone: 'light' as const },  // Aqua 80 → Green 30
  { name: 'Fire', value: 'linear-gradient(135deg, #FF0000 0%, #FF6600 50%, #FFD040 100%)', textTone: 'light' as const },  // Red 50 → Orange 50 → Amber 40
  { name: 'Aurora', value: 'linear-gradient(135deg, #B3F0FF 0%, #FFE6F5 100%)', textTone: 'dark' as const },   // Aqua 20 → Magenta 10
  { name: 'Galaxy', value: 'linear-gradient(135deg, #002680 0%, #0099CC 100%)', textTone: 'light' as const },  // Blue 80 → Aqua 60
  { name: 'Peachy', value: 'linear-gradient(135deg, #FFF0E6 0%, #FFB380 100%)', textTone: 'dark' as const },   // Orange 10 → Orange 30
  { name: 'Lavender', value: 'linear-gradient(135deg, #E8C0FF 0%, #FFF8E6 100%)', textTone: 'dark' as const }, // Purple 20 → Amber 10
  { name: 'Ocean', value: 'linear-gradient(135deg, #66E0FF 0%, #6690FF 100%)', textTone: 'light' as const },   // Aqua 30 → Blue 40
  { name: 'Rose', value: 'linear-gradient(135deg, #CC007F 0%, #FFE6F5 100%)', textTone: 'light' as const },    // Magenta 60 → Magenta 10
  { name: 'Smoky', value: 'linear-gradient(135deg, #1A1A1A 0%, #9E9E9E 100%)', textTone: 'light' as const },   // Gray 110 → Gray 50
];


/** Cookie get utility for enterprise environments where localStorage may be unreliable. */
export function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(';').shift() || null;
  return null;
}

/** Cookie set utility. */
export function setCookie(name: string, value: string): void {
  if (typeof document === 'undefined') return;
  const date = new Date();
  date.setTime(date.getTime() + (365 * 24 * 60 * 60 * 1000));
  const expires = `expires=${date.toUTCString()}`;
  document.cookie = `${name}=${value};${expires};path=/;SameSite=Strict`;
}

/** Returns the saved user bubble color from localStorage or cookie. */
export function getSavedBubbleColor(): string {
  const defaultColor = USER_BUBBLE_COLORS[0].value;
  // Guard for non-browser environments and handle restricted storage.
  if (typeof window !== 'undefined') {
    try {
      const stored = window.localStorage?.getItem('user-bubble-color');
      if (stored) {
        return stored;
      }
    } catch {
      // Ignore storage errors and fall back to cookie/default.
    }
  }
  return getCookie('user-bubble-color') || defaultColor;
}

/**
 * Returns the CSS background and text color for an avatar given the bubble color value and theme mode.
 */
export function getBubbleStyle(
  userBubbleColor: string,
  mode: 'light' | 'dark' | string
): { background: string; color: string } {
  if (userBubbleColor === 'default') {
    return {
      background: mode === 'dark' ? '#313136' : '#e5e7eb',
      color: mode === 'dark' ? '#ffffff' : '#1f2937',
    };
  }
  const meta = USER_BUBBLE_COLORS.find(c => c.value === userBubbleColor);
  const tone = meta?.textTone || 'light';
  const forceDark = tone === 'dark' || (tone === 'auto' && mode === 'light');
  return {
    background: userBubbleColor,
    color: forceDark ? '#1f2937' : '#ffffff',
  };
}
